import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Button,
    Card,
    CardContent,
    Checkbox, Chip, ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl, InputLabel,
    Link, List, ListItem, ListItemText,
    MenuItem,
    Select,
    Table, TableBody, TableCell, TableHead, TableRow,
    Tooltip, Typography
} from "@mui/material";
import { tooltipClasses } from '@mui/material/Tooltip';

import CampaignDetailsDialog from "../../Dialogs/CampaignDetailsDialog";
import HistoryDialog from "../../Dialogs/HistoryDialog";
import CampaignJsonDialog from "../../Dialogs/CampaignJsonDialog";
import Audience from "../Audience";
import {useSetStatusMutation} from "../../../core/reducers/campaign";
import CampaignBundleDialog from "../../Dialogs/CampaignBundleDialog";
import CampaignConfigDialog from "../../Dialogs/CampaignConfigDialog";
import EditIcon from '@mui/icons-material/Edit';
import CopyIcon from '@mui/icons-material/ContentCopy';
import {LoadingButton} from "@mui/lab";
import {CampaignItemContext} from "../../../core/context/context";
import dayjs from "dayjs";

import clipboardCopy from 'clipboard-copy';
import {SnackBarContext} from "../../../core/context/snackBarProvider";
import {useSelector} from "react-redux";
import {getCalendarLink, getLinkByTaskGid, isEmpty, isProdAdmin} from "../../Utils/CommonUtils";
import CampaignBundleRollbackDialog from "../../Dialogs/CampaignBundleRollbackDialog";
import FmdBadIcon from '@mui/icons-material/FmdBad';
import {useSetTemplateStatusMutation} from "../../../core/reducers/templates";
import { styled } from '@mui/material/styles';
import StatusSelector from "../StatusSelector";
import CampaignItemTimeleft from "./CampaignItemTimeleft";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {Box} from "@mui/system";


const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#fff',
        boxShadow: '3px 1px 6px 4px rgba(0,0,0,0.2)',
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 220,
        fontSize: theme.typography.pxToRem(12),
        border: '0px solid #dadde9',
    },
}));

// differences of the campaign with the settings sheet (config check mode)
const ConfigCheckDetails = ({configCheck}) => {
    const differences = configCheck.audiences.flatMap(audience =>
        audience.differences.map(difference => ({audience, difference})));
    return <Box sx={{mb: 1}}>
        <Table size="small" sx={{tableLayout: 'fixed', '& td, & th': {px: 0.5, overflowWrap: 'anywhere'}}}>
            <TableHead>
                <TableRow>
                    <TableCell sx={{width: 70}}>Audience</TableCell>
                    <TableCell sx={{width: 50}}>Row</TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell>Sheet</TableCell>
                    <TableCell>Campaign</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {differences.map(({audience, difference}) =>
                    <TableRow key={`${audience.index}-${difference.key}`}>
                        <TableCell>{audience.index + 1}</TableCell>
                        <TableCell>{audience.sheetRow}</TableCell>
                        <TableCell><code>{difference.key}</code></TableCell>
                        <TableCell><code>{difference.expected ?? '—'}</code></TableCell>
                        <TableCell sx={{color: 'error.main'}}><code>{difference.actual ?? '—'}</code></TableCell>
                    </TableRow>)}
            </TableBody>
        </Table>
    </Box>
}

// isSelected передаётся из списка кампаний (чтобы смена выделения не перерисовывала все карточки через контекст);
// если не передан (шаблоны) — берётся из selectedList в контексте, как раньше.
const CampaignItem = ({item, bundle, rightPanel, showRainbow, isSelected}) => {
    const {onSelectItem, selectedList, env, createDisabled, isTemplate, externalLinks, openCreator} = useContext(CampaignItemContext)

    const { addAlert } = useContext(SnackBarContext);

    const [open, setOpen] = useState(false);
    const [openTooltip, setOpenTooltip] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [bundlesOpen, setBundlesOpen] = useState(false);
    const [jsonOpen, setJsonOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [configOpen, setConfigOpen] = useState(false);

    const [showConfirmStatusChange, setShowConfirmStatusChange] = useState(false)
    const [status, setStatus] = useState('')
    const [prevStatus, setPrevStatus] = useState('')

    const statusList = ["ACTIVE", "SUSPENDED", "HIDDEN", "DEACTIVATED"]

    const selectedFromList = isSelected !== undefined
        ? isSelected
        : (selectedList != undefined ? selectedList.indexOf(item.id) >= 0 : false)
    const [selected, setSelected] = useState(selectedFromList)

    const [bundleHistoryDialog, setBundleHistoryDialog] = useState({
        isOpen:false,
        generation: '',
        bundle: item.bundleId
    })

    const appMode = useSelector((state) => state.data.SourceMode.mode);
    // булево значение вместо всего UserSession: карточка не перерисовывается при каждом обновлении токена
    const canEditStatus = useSelector((state) => isProdAdmin(env, state.data.UserSession));

    useEffect(() => {
        if (isSelected !== undefined || selectedList != undefined)
            setSelected(selectedFromList)
    }, [selectedFromList])

    useEffect(() => {
        setStatus(item.status);
        setPrevStatus(item.status);
    }, [item.status, item.id]);

    const handleCopyClick = async (text) => {
        try {
            await clipboardCopy(text);
            addAlert({text: 'copied', type: 'success'});
        } catch {
            addAlert({text: 'Could not copy to clipboard', type: 'error'});
        }
    };

    const handleSelect = (event) => {
        onSelectItem({item: item, bundle: getBundleInfo()})
        setSelected(!selected)
    }

    const [
        saveStatus,
        saveStatusResponse
    ] = useSetStatusMutation()

    const [
        saveTemplateStatus,
        saveTemplateStatusResponse
    ] = useSetTemplateStatusMutation()

    const responseObj = isTemplate ? saveTemplateStatusResponse : saveStatusResponse

    const links = useMemo(() => {
        const filtered = externalLinks === undefined ? [] : externalLinks.filter(filterItem => filterItem.eventId?.replace('_BR','')?.trim()?.toLowerCase() == item.id?.replace('_BR','').trim()?.toLowerCase())
        return filtered.find(link => link.asana !== null) || filtered[0] || false;
    }, [externalLinks, item.id]);


    const confirmSaveStatus = async () => {
        let data = {
            environment: env,
            id: item.id,
            status: status
        }

        let response = {};
        if(isTemplate) {
            response = await saveTemplateStatus(data)
        } else {
            response = await saveStatus(data)
        }

        if (response.hasOwnProperty('data') && (
            ( !isTemplate&&response.data?.status?.toLowerCase() == status.toLowerCase() ) ||
            ( isTemplate&&response.data.hasOwnProperty('id') )
        ))
            addAlert({text: 'Status changed', type: 'success'})
        else
            addAlert({text: 'Error ' + response?.error?.data?.message, type: 'error'})

        setShowConfirmStatusChange(false)
        setPrevStatus(status)
    }
    const cancelSaveStatus = () => {
        setStatus(prevStatus)
        setShowConfirmStatusChange(false)
    }
    const handleChangeStatus = (event) => {
        if(!canEditStatus) return false;

        setStatus(event.target.value)
        setShowConfirmStatusChange(true)
    }


    const getBundleVersion = useMemo(() => {
        let _bundle = bundle;

        if (item.hasOwnProperty('fixedBundle') && item.fixedBundle != null) {
            _bundle = item.fixedBundle;
        }

        if (_bundle == false) return null;

        const keys = Object.keys(_bundle.generations);
        const last_key = keys.pop();
        const prelast_key = keys.length > 0 ? keys.pop() : false;

        if(_bundle.generations[last_key] == undefined) return <><span>n/a</span></>
        return (
            <>
                <span>g{last_key}/v{_bundle.generations[last_key]['bundleVersion']}</span>
                {prelast_key !== false && <span>g{prelast_key}/v{_bundle.generations[prelast_key]['bundleVersion']}</span>}
            </>
        );
    }, [bundle, item.fixedBundle]);

    const getBundleInfo = () => {
        if (bundle == false) return false


        const keys = Object.keys(bundle.generations)
        const last_key = keys.pop()
        const prelast_key = keys.length > 0 ? keys.pop() : false

        return {
            generation: last_key,
            bundleVersion: bundle.generations[last_key] != undefined ? bundle.generations[last_key]['bundleVersion'] : '',
            prelastGeneration: prelast_key !== false ? prelast_key : '',
            prelastBundleVersion: prelast_key !== false ? bundle.generations[prelast_key]['bundleVersion'] : '',
        }
    }

    // openCreator (из списка) навигирует с publisherReturnTo текущего URL; сама карточка не подписана на location
    const handleCloneCampaignClick = () => {
        if(isTemplate)
            openCreator(`/creator/templates/${item.id}?env=${env}&clone`)
        else
            openCreator(`/creator/${appMode}/${item.id}?env=${env}&clone`)
    }

    const handleEditCampaignClick = () => {
        if(isTemplate) {
            openCreator(`/creator/templates/${item.id}?env=${env}`)
        } else {
            openCreator(`/creator/${appMode}/${item.id}?env=${env}`)
        }

    }

    const selectBundle = (id, item) => {
        setBundleHistoryDialog({
            isOpen: true,
            //bundle: id,
            item: item
        })
    }
    const openBundleHistoryDialog = () => {
        setBundleHistoryDialog({
            ...bundleHistoryDialog,
            isOpen: !bundleHistoryDialog.isOpen
        })
    }

    const warningText = useMemo(() => {
        if (item.warnings?.length > 0) {
            return (
                <div className={"list-warnings"}>
                    {item.warnings.map((warning, index) => (
                        <div key={index}>{index+1}. {warning}</div>
                    ))}
                </div>
            );
        }
        return null;
    }, [item.warnings]);


    const canChangeStatus = () => {
        if(env == "production" && prevStatus=="DEACTIVATED") return false;
        if(env == "production" && prevStatus=="COMPLETED" && status != "DEACTIVATED") return false;

        return true;
    }


    const getChangeStatusPopup = () => {

        if(!canChangeStatus()) {
            return <>You can not change DEACTIVATED status in production: <a target="_blank" href="https://belka-games.atlassian.net/wiki/spaces/BAC/pages/3992617217/Mephisto+Use+Cases#%D0%A1%D1%82%D0%B0%D1%82%D1%83%D1%81%D1%8B-%D0%B0%D0%B1-%D1%82%D0%B5%D1%81%D1%82%D0%BE%D0%B2">docs</a></>
        }


        if(status=="COMPLETED") return <div></div>

    }


    const handleTooltipClose = () => {
        setOpenTooltip(false);
    };

    const handleTooltipToggle = () => {
        setOpenTooltip(!openTooltip);
    };


    const handleExpandBody = useCallback((e) => {
        const ignoredTags = ['button', 'input'];
        const ignoredClasses = [
            'MuiChip-label',
            'campaign-actions',
            'campaign-item__checkbox',
            'campaign-status',
            'btn-wrap'
        ];

        if (
            typeof(e.target.className)=='string' &&
            !ignoredTags.includes(e.target.tagName.toLowerCase()) &&
            !ignoredClasses.some(cls => e.target.className.includes(cls))
        ) {
            setShowDetails(prev => !prev);
        }
    }, []);

    return (
        <>
            <Card className="campaign-item" key={item.id} style={{marginBottom: '3px'}}>
                <CardContent>


                    <div className="campaign-item__body" onClick={handleExpandBody}>
                        <div >
                            <div className="campaign-item__header">
                                <div className="campaign-item__checkbox">
                                    {onSelectItem != undefined && <Checkbox onChange={handleSelect} checked={selected}/>}
                                </div>

                                {(env=='liveops'||env=='production')&&(item.hasOwnProperty("warnings")&&item.warnings.length>0) && <div className="campaign-item__warning">
                                    <ClickAwayListener onClickAway={handleTooltipClose}>
                                        <div>
                                            <HtmlTooltip
                                                PopperProps={{
                                                    disablePortal: true,
                                                }}
                                                onClose={handleTooltipClose}
                                                open={openTooltip}
                                                disableFocusListener
                                                disableHoverListener
                                                disableTouchListener
                                                title={warningText}
                                            >
                                                <FmdBadIcon
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent the click from reaching ClickAwayListener
                                                        handleTooltipToggle(); // Toggle the tooltip open state
                                                    }}
                                                    color="warning"
                                                />
                                            </HtmlTooltip>
                                        </div>
                                    </ClickAwayListener>
                                </div>}

                                <div className={`campaign-item__name ${showDetails ? 'expanded' : ''}`}>

                                    <div >


                                        <span onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyClick(item.id)
                                        }}>{item.id}</span>

                                        {item.configCheck && <Chip size="small" color="error" sx={{ml: 1}}
                                            label={`${item.configCheck.audiences.reduce((count, audience) => count + audience.differences.length, 0)} differences`}/>}

                                        {showDetails&&<div className={"campaign-item__groupName"}>
                                            {item.groupId}
                                        </div>}

                                        {showDetails&&(item.tags!=null&&item.tags.length>0)&&<div className={"campaign-item__name-tagsWrapper"}>{item.tags.map(tag=>(<div key={tag} className={"campaign-item__name-tag"}>{tag}</div>))}</div>}

                                    </div>


                                </div>

                                <div className="campaign-gens">
                                    <div className="campaign-item__tags">
                                        {getBundleVersion}
                                        {bundle?.balanceLink &&
                                            <span>
                                                <Link
                                                    href={bundle.balanceLink}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    color="inherit"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    balance
                                                </Link>
                                            </span>}
                                    </div>
                                </div>

                                {env!=='production' && <div className="campaign-actions">
                                    <div>
                                        {!createDisabled ?
                                            <Link
                                                onClick={handleEditCampaignClick}
                                                  className="campaign-edit__link">
                                                <Tooltip title={"edit"}
                                                         placement="right">
                                                    <EditIcon fontSize="small"/>
                                                </Tooltip>
                                            </Link> :
                                            <Tooltip title={"editing disabled"}
                                                     placement="right">
                                                <EditIcon fontSize="small"/>
                                            </Tooltip>
                                        }
                                    </div>

                                    <div>
                                        {!createDisabled ?
                                            <Link
                                                onClick={handleCloneCampaignClick}
                                                  className="campaign-edit__link">
                                                <Tooltip title={"clone"} placement="right">
                                                    <CopyIcon fontSize="small"/>
                                                </Tooltip>
                                            </Link>
                                            :
                                            <Tooltip title={"create disabled"} placement="right">
                                                <CopyIcon fontSize="small"/>
                                            </Tooltip>
                                        }



                                    </div>
                                </div>}

                                <CampaignItemTimeleft item={item} showDetails={showDetails} showRainbow={showRainbow} />

                                <div className="campaign-status">
                                    <StatusSelector
                                        status={status}
                                        statusList={statusList}
                                        onChange={handleChangeStatus}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            return true;
                                        }}
                                    />

                                </div>

                            </div>

                        </div>

                        {isTemplate && <><div className="campaign-details"><small><b>Connected campaigns</b></small></div>
                            <small>{item.campaigns.map((_c, index)=> (
                                <div key={index}>{_c}</div>
                            ))}</small></>}


                        {showDetails && <div className="campaign-details">

                            {item.bundleId && <Box className="campaign-item__bundleName" sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1}}>
                                <Typography variant="body2" color="text.secondary">Bundle ID:</Typography>
                                <Tooltip title="Copy Bundle ID">
                                    <Chip size="small" variant="outlined" label={item.bundleId} onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyClick(item.bundleId);
                                    }}/>
                                </Tooltip>
                            </Box>}

                            {item.configCheck && <ConfigCheckDetails configCheck={item.configCheck}/>}

                            {(item.hasOwnProperty("warnings")&&item.warnings.length>0) && <Alert className={"campaign-warning-alert"} sx={{p:1}} severity={"warning"}>
                                {warningText}
                            </Alert>}

                            {!isEmpty(item.previuosCampaignId) && <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1}}>
                                <Typography variant="body2" color="text.secondary">Previous Campaign ID:</Typography>
                                <Tooltip title={item.previuosCampaignId}>
                                    <Chip size="small" variant="outlined" label={item.previuosCampaignId}/>
                                </Tooltip>
                            </Box>}


                            {(item.tournamentId != null || item.matchmakingType != null) && <Box
                                component="dl"
                                sx={{display: 'flex', flexDirection: 'column', gap: 1, mt: 0, mb: 1.5}}
                            >
                                {[
                                    ['Tournament ID', item.tournamentId],
                                    ['Matchmaking Type', item.matchmakingType]
                                ].filter(([, value]) => value != null).map(([label, value]) => (
                                    <Box key={label} sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0}}>
                                        <Typography component="dt" variant="body2" color="text.secondary" sx={{minWidth: 132}}>
                                            {label}
                                        </Typography>
                                        <Box component="dd" sx={{m: 0, minWidth: 0, maxWidth: '100%'}}>
                                            <Tooltip title="Click to copy">
                                                <Chip
                                                    component="button"
                                                    type="button"
                                                    size="small"
                                                    variant="outlined"
                                                    label={value}
                                                    aria-label={`Copy ${label}: ${value}`}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleCopyClick(value);
                                                    }}
                                                    sx={{
                                                        maxWidth: '100%',
                                                        height: 'auto',
                                                        borderRadius: '16px',
                                                        '& .MuiChip-label': {px: 1.5, py: 0.5, whiteSpace: 'normal', overflowWrap: 'anywhere'}
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>}

                            <div className="campaign-audience">
                                {item.audiences.map((audience, index) => (
                                    <Audience key={index} audience={audience}>Audience {(index + 1)}<br /></Audience>
                                ))}
                            </div>
                            <div className="campaign-audience right">
                                {item.fickle==true&&<Chip size={"small"} color="warning" label={"Check audiences Every Time (fickle)"} />}
                                {item.repeatable==true&&<Chip sx={{ml:0.2}} size={"small"} color="warning" label={"Allow repeat after finish (repeatable)"} />}
                            </div>


                            <div className="campaign-item__footer">
                               <Box display={'flex'}>
                                   {bundle != undefined &&
                                       <Button variant="outlined" onClick={() => (setBundlesOpen(true))} sx={{mr: 1}}>
                                           Bundles
                                       </Button>}
                                   <Button variant="outlined" onClick={() => (setJsonOpen(true))} sx={{mr: 1}}>
                                       JSON
                                   </Button>
                                   <Button variant="outlined" onClick={() => (setHistoryOpen(true))} sx={{mr: 1}}>
                                       History
                                   </Button>
                                   {item.config &&
                                       <Button variant="outlined" onClick={() => setConfigOpen(true)} sx={{mr: 1}}>
                                           Config
                                       </Button>}


                                   {<Box className={'btn-wrap'} sx={{ display: 'flex', marginLeft: 'auto' }}>
                                       <Button disabled={links == false || links?.asana==null} color="error" variant="outlined"
                                               onClick={() => window.open(getLinkByTaskGid(links.asana), '_blank')}
                                               sx={{ mr: 1, pl: 1 }}>
                                           <WorkspacesIcon sx={{ mr: 1 }} /> ASANA
                                       </Button>
                                       <Button variant="outlined"
                                               disabled={links == false || links.id==''}
                                               onClick={() => window.open(getCalendarLink(links.id), '_blank')}
                                               sx={{ pl: 1 }}>
                                           <CalendarMonthIcon sx={{ mr: 1 }} /> Calendar
                                       </Button>
                                   </Box>}
                               </Box>
                            </div>
                        </div>}
                    </div>
                </CardContent>
            </Card>

            {(bundle != undefined && bundlesOpen) &&
                <CampaignBundleDialog key={`cbd`+item.id} selectBundle={selectBundle} fixedBundle={item.hasOwnProperty('fixedBundle')&&item.fixedBundle!=null}
                                      bundleItem={item.hasOwnProperty('fixedBundle')&&item.fixedBundle!=null?item.fixedBundle:bundle} open={bundlesOpen} setOpen={setBundlesOpen} campaign={item.id}
                                      env={env} isTemplate={isTemplate}></CampaignBundleDialog>}
            {item.config && configOpen &&
                <CampaignConfigDialog campaign={item} open={configOpen} setOpen={setConfigOpen} />}
            {(historyOpen) &&
                <HistoryDialog key={`h`+item.id} env={env} item={item} open={historyOpen} setOpen={setHistoryOpen}></HistoryDialog>}
            {open&&<CampaignDetailsDialog key={`cdd`+item.id} item={item} open={open} setOpen={setOpen}></CampaignDetailsDialog>}
            {jsonOpen&&<CampaignJsonDialog key={`cjd`+item.id} item={item} open={jsonOpen} setOpen={setJsonOpen}></CampaignJsonDialog>}
            {bundleHistoryDialog.isOpen&&<CampaignBundleRollbackDialog key={`cbrd`+item.id} env={env} bundle={item.bundleId} selectedBundle={bundleHistoryDialog} open={bundleHistoryDialog.isOpen} setOpen={openBundleHistoryDialog} ></CampaignBundleRollbackDialog>}

            {showConfirmStatusChange&&<Dialog
                open={showConfirmStatusChange}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Changing status
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" component="div">
                        You want to change status from <strong>{prevStatus}</strong> to <strong>{status}</strong>
                        <br/>for <strong>{item.id}</strong>

                        <div>{getChangeStatusPopup()}</div>

                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelSaveStatus}>Cancel</Button>

                    {canChangeStatus()&&<LoadingButton variant="contained" onClick={confirmSaveStatus}
                                   loading={responseObj.isLoading}>
                        Save
                    </LoadingButton>}
                </DialogActions>
            </Dialog>}
        </>
    )

}

export default React.memo(CampaignItem);
